import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Search, Settings } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORY_META, CLIENT_SECTIONS, MAIN_SECTIONS, REGION_LABELS, statusesFor } from "../lib/options";
import { ROLES } from "../components/forms/CaregiverApplication";
import { Logo } from "../components/site/Logo";
import { Input } from "../components/ui/input";
import { SubmissionTable } from "../components/admin/SubmissionTable";
import { SubmissionDetail } from "../components/admin/SubmissionDetail";
import { SettingsDialog } from "../components/admin/SettingsDialog";

const EMPTY = { region: "", status: "", q: "", interest_list: "", role: "" };

const SideGroup = ({ title, keys, category, setCategory, stats }) => (
  <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" aria-label={title}>
    <p className="hidden lg:block px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.2em] font-bold text-[#5A6561]">{title}</p>
    {keys.map((key) => {
      const meta = CATEGORY_META[key];
      const s = stats[key] || { total: 0, new: 0 };
      const active = key === category;
      return (
        <button key={key} type="button" onClick={() => setCategory(key)} data-testid={`admin-tab-${key.replace(/_/g, "-")}`}
          className={`shrink-0 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm text-left transition-colors ${active ? "bg-[#1E3A2B] text-[#FAF8F5]" : "hover:bg-white text-[#1C2421]"}`}>
          <span className="font-medium whitespace-nowrap lg:whitespace-normal">{meta.label}</span>
          <span className="flex items-center gap-1.5">
            {s.new > 0 && <span className="whitespace-nowrap rounded-full bg-[#C86D51] text-white text-[10px] font-bold px-2 py-0.5" data-testid={`admin-tab-new-${key}`}>{s.new} new</span>}
            <span className={`text-xs ${active ? "text-[#E5E0D8]" : "text-[#5A6561]"}`}>{s.total}</span>
          </span>
        </button>
      );
    })}
  </nav>
);

const Filters = ({ category, filters, setFilters, onExport }) => {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const housing = ["housing", "residential_support"].includes(category);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6561]" />
        <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search name, email, city or reference" className="pl-9 h-10 bg-white" data-testid="admin-search-input" />
      </div>
      <select value={filters.region} onChange={(e) => set("region", e.target.value)} className="field-select !h-10 !w-auto" data-testid="admin-filter-location-select" aria-label="Filter by location">
        <option value="">All locations</option>
        {Object.entries(REGION_LABELS).filter(([v]) => ["gta", "other"].includes(v)).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select value={filters.status} onChange={(e) => set("status", e.target.value)} className="field-select !h-10 !w-auto" data-testid="admin-filter-status-select" aria-label="Filter by status">
        <option value="">All statuses</option>
        {statusesFor(category).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {category === "caregiver_application" && (
        <select value={filters.role} onChange={(e) => set("role", e.target.value)} className="field-select !h-10 !w-auto" data-testid="admin-filter-role-select" aria-label="Filter by position">
          <option value="">All positions</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      )}
      {housing && (
        <select value={filters.interest_list} onChange={(e) => set("interest_list", e.target.value)} className="field-select !h-10 !w-auto" data-testid="admin-filter-interest-select" aria-label="Filter by interest list">
          <option value="">All inquiries</option>
          <option value="true">On interest list</option>
          <option value="false">Not on interest list</option>
        </select>
      )}
      <button type="button" onClick={onExport} className="btn-pill btn-outline !py-2" data-testid="admin-export-csv-button"><Download className="h-4 w-4" /> Export CSV</button>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [category, setCategory] = useState("caregiver_application");
  const [filters, setFilters] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const params = useCallback(() => {
    const p = { category };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [category, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([api.get("/admin/submissions", { params: params() }), api.get("/admin/stats")]);
      setItems(list.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.q]);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref) return;
    api.get("/admin/submissions", { params: { q: ref } }).then(({ data }) => {
      const hit = data.find((d) => d.reference === ref);
      if (hit) {
        setCategory(hit.category);
        setSelected(hit.id);
      }
    }).catch(() => {});
  }, []);

  const changeCategory = (c) => { setCategory(c); setFilters(EMPTY); };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `friendship-pen-${category}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F3EFEA]" data-testid="admin-dashboard">
      <header className="bg-[#FAF8F5] border-b border-[#E5E0D8]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4"><Logo /><span className="hidden md:inline text-xs uppercase tracking-[0.2em] font-bold text-[#B25B41]">Admin</span></div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm text-[#5A6561]" data-testid="admin-user-email">{user?.email}</span>
            <button type="button" onClick={() => setSettingsOpen(true)} className="btn-pill btn-outline !px-3 !py-2" aria-label="Data settings" data-testid="admin-settings-button"><Settings className="h-4 w-4" /></button>
            <button type="button" onClick={logout} className="btn-pill btn-outline !py-2" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[300px_1fr] gap-6">
        <aside className="space-y-2">
          <SideGroup title="Main sections" keys={MAIN_SECTIONS} category={category} setCategory={changeCategory} stats={stats} />
          <SideGroup title="Client inquiries" keys={CLIENT_SECTIONS} category={category} setCategory={changeCategory} stats={stats} />
        </aside>
        <section className="space-y-4 min-w-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="admin-category-title">{CATEGORY_META[category].label}</h1>
            <p className="text-sm text-[#5A6561] mt-1">{category === "caregiver_application" ? "Review candidates, AI resume match scores and decisions." : statusesFor(category)[0] === "Awaiting Response" ? "New inquiries start as Awaiting Response. Mark Response Sent once you have replied." : "Track follow-up status and internal notes for each inquiry."}</p>
          </div>
          <Filters category={category} filters={filters} setFilters={setFilters} onExport={exportCsv} />
          <SubmissionTable items={items} loading={loading} onOpen={setSelected} />
        </section>
      </div>
      <SubmissionDetail id={selected} onClose={() => setSelected(null)} onChanged={load} />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onPurged={load} />
    </div>
  );
}
